import React, { useEffect, useState } from 'react';
import axios from 'axios';
import TradeList   from './realtrade/TradeList';
import AptTrend    from './realtrade/AptTrend';
import RegionStats from './realtrade/RegionStats';
import NewTrades   from './realtrade/NewTrades';
import AptScreen from './realtrade/AptScreen';
import ShTrade from './realtrade/ShTrade';
import TradeVolume from './realtrade/TradeVolume';   // [RT_VOLUME_V1] 거래량 탭

// ─────────────────────────────────────────────
// 부동산 실거래 조회 (관리자 전용)
//
// [역할]
//   4개 탭을 감싸는 컨테이너. 지역 목록과 기간을 상위에서 한 번만 불러와
//   각 탭에 내려준다(탭 전환할 때마다 재조회하지 않기 위함).
//
// [연관]
//   - 백엔드 routes/realTrades.js (/api/real-trades/*)
//   - App.js 에서 <Route path="/real-trades"> 로 연결
//   - Header.jsx NAV_ITEMS 에 메뉴 등록
// ─────────────────────────────────────────────

// 인증 헤더 (UserGradeManager.jsx 와 동일한 패턴)
export const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

// 만원 단위 금액을 "3억 5,000" 형태로 (부동산 실무 표기)
export function fmtAmt(manwon) {
  if (manwon == null) return '-';
  const eok = Math.floor(manwon / 10000);
  const rest = manwon % 10000;
  if (eok === 0) return `${rest.toLocaleString()}만`;
  if (rest === 0) return `${eok}억`;
  return `${eok}억 ${rest.toLocaleString()}`;
}

// 전용면적 ㎡ → 평 (소수 1자리)
export const toPyeong = (m2) => (m2 / 3.305785).toFixed(1);

// 최근 N개월의 YYYYMM (기본 기간 설정용)
function ymAgo(n) {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const TABS = [
  { key: 'list',   label: '📋 거래목록' },
  { key: 'screen', label: '🔎 단지검색' },
  { key: 'apt',    label: '📈 단지분석' },
  { key: 'region', label: '📊 지역통계' },
  { key: 'volume', label: '📶 거래량' },   // [RT_VOLUME_V1]
  { key: 'new',    label: '🆕 신규등록' },
];

export default function RealTradePage() {
  const [tab, setTab] = useState('list');
  const [regions, setRegions] = useState([]);
  const [zones, setZones] = useState([]);

  // 지역 선택은 2단계.
  //  1차 zoneSel  : 권역 (서울동남 등)
  //  2차 regionSel: 그 권역 안의 시군구. 빈 값이면 권역 전체를 조회한다.
  const [zoneSel, setZoneSel] = useState('');
  const [regionSel, setRegionSel] = useState('');

  // 부동산 유형 - 모든 탭이 공유한다.
  // 유형을 섞으면 평당가·매매율 비교가 무의미해지므로 항상 하나만 선택한다.
  const [propType, setPropType] = useState('AP');

  // 단지검색(스크리닝) 결과를 클릭하면 단지분석 탭으로 넘어가 해당 단지를 연다.
  // pickedApt 가 바뀌면 AptTrend 가 useEffect 로 감지해 조회를 시작한다.
  const [pickedApt, setPickedApt] = useState(null);

  const goApt = (apt) => {
    // 같은 단지를 다시 눌러도 반응하도록 타임스탬프를 붙인다
    setPickedApt({ ...apt, _ts: Date.now() });
    setTab('apt');
  };

  // 공통 필터 - 각 탭이 공유한다
  const [lawdCd, setLawdCd] = useState('');
  const [zone, setZone] = useState('');
    const [from, setFrom] = useState(ymAgo(23));   // 최근 24개월 (전체 보유 기간)
  const [to, setTo]     = useState(ymAgo(0));

  const [err, setErr] = useState('');

  // 지역 목록은 한 번만 로드
  useEffect(() => {
    axios.get('/api/real-trades/regions', auth())
      .then(res => {
        const list = res.data.regions || [];
        setRegions(list);
        setZones(res.data.zones || []);
        const zs = res.data.zones || [];
        if (zs.length && !zoneSel) {
          // 첫 권역 전체로 시작한다
          setZoneSel(zs[0].zone);
          setZone(zs[0].zone);
          setLawdCd('');
          setRegionSel('');
        }
      })
      .catch(e => setErr(e.response?.data?.error || e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 1차 권역 변경 → 2차 목록을 그 권역의 구로 바꾸고, 권역 전체로 조회
  const onZoneChange = (z) => {
    setZoneSel(z);
    setRegionSel('');
    setZone(z);
    setLawdCd('');
  };

  // 2차 시군구 변경 → 빈 값이면 권역 전체, 아니면 해당 구만
  const onRegionChange = (v) => {
    setRegionSel(v);
    if (v) { setLawdCd(v); setZone(''); }
    else   { setLawdCd(''); setZone(zoneSel); }
  };

  // 현재 권역에 속한 시군구만 추린다
  const zoneRegions = regions.filter(r => r.zone === zoneSel);

  // 하위 탭에 공통으로 넘길 값 묶음
  const shared = { lawdCd, zone, from, to, regions, propType };

  return (
    <div style={{ padding: 16, maxWidth: 1400, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 4px' }}>📊 부동산 실거래 조회</h2>
      <p style={{ margin: '0 0 16px', fontSize: 12, color: '#64748b' }}>
        국토교통부 실거래가 자료 · <b>매매</b>만 · 해제거래는 기본 제외 · 평당가는 전용면적 기준
      </p>

      {err && (
        <div style={{ padding: 10, background: '#fee2e2', color: '#991b1b',
                      borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
          ⚠️ {err}
        </div>
      )}

      {/* ── 공통 필터: 지역 + 기간 ── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
                    padding: 12, background: '#f8fafc', borderRadius: 8, marginBottom: 12 }}>
        <label style={lbl}>유형</label>
        <select value={propType} onChange={e => setPropType(e.target.value)} style={sel}>
          <option value="AP">🏢 아파트</option>
          <option value="OF">🏬 오피스텔</option>
          <option value="RH">🏘️ 연립·다세대</option>
          <option value="SH">🏠 단독·다가구</option>
        </select>

        <label style={lbl}>지역</label>
        <select value={zoneSel} onChange={e => onZoneChange(e.target.value)} style={sel}>
          {zones.map(z => (
            <option key={z.zone} value={z.zone}>
              🗺️ {z.zone} ({z.cnt})
            </option>
          ))}
        </select>

        <select value={regionSel} onChange={e => onRegionChange(e.target.value)} style={sel}>
          <option value="">{zoneSel} 전체</option>
          {zoneRegions.map(r => (
            <option key={r.lawd_cd} value={r.lawd_cd}>{r.sgg_nm}</option>
          ))}
        </select>

        <label style={lbl}>기간</label>
        <input value={from} onChange={e => setFrom(e.target.value)}
               placeholder="202509" style={{ ...inp, width: 90 }} />
        <span style={{ color: '#94a3b8' }}>~</span>
        <input value={to} onChange={e => setTo(e.target.value)}
               placeholder="202608" style={{ ...inp, width: 90 }} />
        <span style={{ fontSize: 11, color: '#94a3b8' }}>YYYYMM 형식</span>
      </div>

      {/* ── 탭 ── */}
      <div style={{ display: propType === 'SH' ? 'none' : 'flex',
                    gap: 4, borderBottom: '2px solid #e2e8f0', marginBottom: 16 }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              padding: '10px 18px', border: 'none', cursor: 'pointer', fontSize: 14,
              background: tab === t.key ? '#3b82f6' : 'transparent',
              color: tab === t.key ? '#fff' : '#475569',
              borderRadius: '6px 6px 0 0',
              fontWeight: tab === t.key ? 600 : 400,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* 탭별 화면 - key 를 주어 탭 전환 시 상태가 초기화되게 한다 */}
      {/* 단독·다가구는 지번 마스킹·면적 체계가 달라 전용 화면을 쓴다 */}
      {/* 탭을 조건부로 제거하면 React 가 컴포넌트를 파괴해 조회 결과가 사라진다.
          모두 렌더링하되 CSS 로 숨겨서 탭을 오가도 결과가 유지되게 한다.
          유형·지역이 바뀌면 key 가 달라져 새로 만들어진다(그건 의도된 초기화). */}
      {propType === 'SH' ? (
        <ShTrade key={`sh-${lawdCd}`} {...shared} />
      ) : (
        <>
          <div style={{ display: tab === 'list'   ? 'block' : 'none' }}>
            <TradeList key={`l-${propType}-${lawdCd}${zone}`} {...shared} />
          </div>
          <div style={{ display: tab === 'screen' ? 'block' : 'none' }}>
            <AptScreen key={`s-${propType}`} {...shared}
                       onPickApt={goApt} />
          </div>
          <div style={{ display: tab === 'apt'    ? 'block' : 'none' }}>
            <AptTrend key={`a-${propType}-${lawdCd}${zone}`} {...shared}
                      pickedApt={pickedApt} />
          </div>
          <div style={{ display: tab === 'region' ? 'block' : 'none' }}>
            <RegionStats key={`r-${propType}-${lawdCd}${zone}`} {...shared} />
          </div>
          {/* [RT_VOLUME_V1] 거래량 탭 — 권역·시·구·동·단지·평형 월별 거래건수 */}
          <div style={{ display: tab === 'volume' ? 'block' : 'none' }}>
            <TradeVolume key={`v-${propType}-${lawdCd}${zone}`} {...shared}
                         pickedApt={pickedApt} />
          </div>
          <div style={{ display: tab === 'new'    ? 'block' : 'none' }}>
            <NewTrades key={`n-${propType}-${lawdCd}${zone}`} {...shared} />
          </div>
        </>
      )}
    </div>
  );
}

// 공용 스타일 (하위 컴포넌트에서도 import 해서 쓴다)
export const lbl = { fontSize: 13, color: '#475569', fontWeight: 600 };
export const inp = { padding: '6px 8px', border: '1px solid #cbd5e1',
                     borderRadius: 4, fontSize: 13 };
export const sel = { ...inp, minWidth: 150 };
export const card = { background: '#fff', border: '1px solid #e2e8f0',
                      borderRadius: 8, padding: 16, marginBottom: 16 };

// ─────────────────────────────────────────────
// 면적 범위 입력 (㎡ 입력 → 평 자동 표시)
//
//  미리 정해둔 평형 구간으로는 원하는 검색이 안 되는 경우가 많아
//  ㎡ 범위를 직접 입력받는다.
//  입력값 옆에 평 환산값을 즉시 보여줘 감을 잡기 쉽게 한다.
//  1평 = 3.305785㎡
// ─────────────────────────────────────────────
export const M2_PER_PY = 3.305785;
export const m2ToPy = (m2) => (Number(m2) / M2_PER_PY).toFixed(1);

// 자주 쓰는 평형 → ㎡ 범위 (버튼 한 번으로 채워 넣기)
const AREA_PRESETS = [
  { t: '10평대', min: 33,  max: 56  },
  { t: '20평대', min: 56,  max: 89  },
  { t: '30평대', min: 89,  max: 122 },
  { t: '40평대', min: 122, max: 155 },
  { t: '50평↑',  min: 155, max: ''  },
];

export function AreaRangeInput({ arMin, arMax, onMin, onMax }) {
  const py = (v) => (v !== '' && !isNaN(v) ? `${m2ToPy(v)}평` : '');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ ...lbl, width: 100 }}>전용면적</span>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <input value={arMin} onChange={e => onMin(e.target.value.replace(/[^0-9.]/g, ''))}
                 placeholder="이상" style={{ ...inp, width: 80 }} />
          <span style={pyHint}>{py(arMin)}</span>
        </div>

        <span style={{ color: '#94a3b8', alignSelf: 'flex-start', marginTop: 8 }}>~</span>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <input value={arMax} onChange={e => onMax(e.target.value.replace(/[^0-9.]/g, ''))}
                 placeholder="이하" style={{ ...inp, width: 80 }} />
          <span style={pyHint}>{py(arMax)}</span>
        </div>

        <span style={{ fontSize: 12, color: '#64748b', alignSelf: 'flex-start', marginTop: 8 }}>
          ㎡
        </span>
      </div>

      {/* 빠른 입력 - 흔히 쓰는 평형대를 ㎡ 범위로 채워준다 */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginLeft: 106 }}>
        {AREA_PRESETS.map(p => (
          <button key={p.t} type="button"
            onClick={() => { onMin(String(p.min)); onMax(String(p.max)); }}
            style={presetBtn}>
            {p.t}
          </button>
        ))}
        <button type="button" onClick={() => { onMin(''); onMax(''); }} style={presetBtn}>
          전체
        </button>
      </div>
    </div>
  );
}

const pyHint    = { fontSize: 10, color: '#3b82f6', textAlign: 'right',
                    paddingRight: 2, height: 12 };
const presetBtn = { padding: '3px 9px', fontSize: 11, cursor: 'pointer',
                    borderRadius: 10, border: '1px solid #e2e8f0',
                    background: '#f8fafc', color: '#475569' };
